import React, { useState, useEffect, useRef } from 'react';
import { FileSpreadsheet, Calendar, Download, RefreshCw, Save, Plus, Edit2, Trash2, Eye, CheckCircle, History, X, Camera, FileText, FileDown, Settings, Send, Users, Link2, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ComisionesReferenteBadge, comisionesFilasPDF, comisionesFilasPDFClub } from '../components/ComisionesReferente';
import { BlackjackCell, bjPorcentaje, bjValor } from '../components/ConsolidacionBlackjack';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Consolidacion() {
  const [loading, setLoading] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [cierres, setCierres] = useState([]);
  const [showModalConsolidar, setShowModalConsolidar] = useState(false);
  const [showHistorico, setShowHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);
  const userStored = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = userStored.role !== 'worker';

  const screenshotRefTotal = useRef(null); // Ref para captura del total
  
  // Estado para porcentajes de administración por club (configurable y guardado)
  const [porcentajesAdmin, setPorcentajesAdmin] = useState({}); // { club_id: porcentaje }
  const [editandoAdminClub, setEditandoAdminClub] = useState(null); // club_id siendo editado
  
  // Datos manuales para consolidar
  const [compJackTotal, setCompJackTotal] = useState(0);
  const [overlayTotal, setOverlayTotal] = useState(0);
  const [devolucionPerdidasTotal, setDevolucionPerdidasTotal] = useState(0);

  // ==================== ESTADOS PARA SUB-CLUBS ====================
  const [activeTab, setActiveTab] = useState("cierre"); // "cierre" | "config"
  const [showConfigSubClubs, setShowConfigSubClubs] = useState(false);
  const [clientesUnited, setClientesUnited] = useState([]);
  const [clubsDisponibles, setClubsDisponibles] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedSubClubs, setSelectedSubClubs] = useState([]);
  const [selectedClubPrincipal, setSelectedClubPrincipal] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [enviandoCierres, setEnviandoCierres] = useState(false);
  // Envío de cierres a Telegram (2026-06): estado por cierre+club
  const [enviandoClub, setEnviandoClub] = useState(null);
  const [enviosCierre, setEnviosCierre] = useState({});

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Obtener porcentaje admin de un club (individual o predeterminado 10%)
  const getPorcentajeAdmin = (club_id) => {
    return porcentajesAdmin[club_id] || 10;
  };

  // Calcular ADMIN automáticamente basado en % de CUOTA (NEGATIVO porque se resta)
  const calcularAdmin = (cuota, club_id) => {
    const porcentaje = getPorcentajeAdmin(club_id);
    return -1 * (cuota * porcentaje) / 100; // NEGATIVO
  };

  // Guardar porcentaje admin de un club específico en localStorage
  const guardarPorcentajeAdminClub = (club_id, porcentaje) => {
    const nuevos = { ...porcentajesAdmin, [club_id]: porcentaje };
    setPorcentajesAdmin(nuevos);
    localStorage.setItem('porcentajes_admin_clubs', JSON.stringify(nuevos));
    setEditandoAdminClub(null);
    toast.success(`Admin configurado: ${porcentaje}% para club ${club_id}`);
  };

  // Cargar porcentajes admin desde localStorage
  useEffect(() => {
    const saved = localStorage.getItem('porcentajes_admin_clubs');
    if (saved) {
      try {
        setPorcentajesAdmin(JSON.parse(saved));
      } catch (e) {
        console.error('Error cargando porcentajes:', e);
      }
    }
  }, []);

  // Cargar datos calculados desde el backend

  // ==================== FUNCIONES PARA SUB-CLUBS ====================
  
  // Cargar clientes de UNITED
  const loadClientesUnited = async () => {
    try {
      const res = await axios.get(`${API}/cierre/clientes-united`);
      setClientesUnited(res.data.clientes || []);
    } catch (err) {
      console.error("Error cargando clientes:", err);
    }
  };
  
  // Cargar clubs disponibles
  const loadClubsDisponibles = async () => {
    try {
      const res = await axios.get(`${API}/cierre/clubs-disponibles`);
      setClubsDisponibles(res.data.clubs || []);
    } catch (err) {
      console.error("Error cargando clubs:", err);
    }
  };
  
  // Cargar relaciones existentes
  const loadRelationships = async () => {
    try {
      const res = await axios.get(`${API}/cierre/relationships`);
      setRelationships(res.data || []);
    } catch (err) {
      console.error("Error cargando relaciones:", err);
    }
  };
  
  // Guardar configuración de sub-clubs
  const guardarConfigSubClubs = async () => {
    if (!selectedCliente) {
      toast.error("Selecciona un cliente");
      return;
    }
    if (!selectedClubPrincipal) {
      toast.error("Selecciona un club principal");
      return;
    }
    setLoadingConfig(true);
    try {
      const subClubsData = selectedSubClubs.map(clubId => {
        const club = clubsDisponibles.find(c => c.club_id === clubId);
        return { club_id: clubId, club_name: club?.club_name || clubId };
      });
      const clubPrincipal = clubsDisponibles.find(c => c.club_id === selectedClubPrincipal);
      await axios.post(`${API}/cierre/relationships`, {
        cliente_id: selectedCliente,
        club_id: selectedClubPrincipal,
        club_name: clubPrincipal?.club_name || selectedClubPrincipal,
        sub_clubs: subClubsData
      });
      toast.success("Configuración guardada");
      loadRelationships();
      setSelectedCliente("");
      setSelectedSubClubs([]);
      setSelectedClubPrincipal("");
    } catch (err) {
      toast.error("Error guardando configuración");
      console.error(err);
    } finally {
      setLoadingConfig(false);
    }
  };
  
  // Eliminar relación
  const eliminarRelationship = async (id) => {
    if (!window.confirm("¿Eliminar esta configuración?")) return;
    try {
      await axios.delete(`${API}/cierre/relationships/${id}`);
      toast.success("Configuración eliminada");
      loadRelationships();
    } catch (err) {
      toast.error("Error eliminando");
    }
  };
  
  // Ejecutar cierre y enviar a Telegram
  const ejecutarCierreTelegram = async (clienteId, clienteName) => {
    if (!fechaDesde || !fechaHasta) {
      toast.error("Selecciona las fechas del cierre");
      return;
    }
    if (!window.confirm(`¿Enviar PDFs de cierre a Telegram para ${clienteName}?\n\nEsto agendará los movimientos y enviará los PDFs al grupo.`)) return;
    
    setEnviandoCierres(true);
    try {
      const res = await axios.post(
        `${API}/cierre/ejecutar/${clienteId}?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`
      );
      if (res.data.success) {
        toast.success(`✅ ${res.data.mensaje}`);
      } else {
        toast.error("Error en el cierre");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error ejecutando cierre");
      console.error(err);
    } finally {
      setEnviandoCierres(false);
    }
  };
  
  // Cargar datos al montar
  useEffect(() => {
    loadClientesUnited();
    loadClubsDisponibles();
    loadRelationships();
  }, []);

  const loadDatosCalculados = async () => {
    if (!fechaDesde || !fechaHasta) {
      toast.error('Selecciona el rango de fechas');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/poker/consolidacion/calcular`, {
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta }
      });
      
      setCierres(response.data.clubs || []);
      toast.success(`${response.data.clubs?.length || 0} clubs cargados`);
      // Marcar los clubs de este período cuyo cierre ya se envió a Telegram
      try {
        const { data } = await axios.get(`${API}/cierre/enviados`);
        const mapa = {};
        (data.enviados || []).forEach((e) => {
          mapa[`${e.cierre_id}:${e.club_id}`] = { enviado: true, enviado_at: e.enviado_at };
        });
        setEnviosCierre(mapa);
      } catch (e) {
        console.error('No pude cargar los cierres ya enviados:', e);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar datos calculados');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (id, field, value) => {
    setCierres(prev => prev.map(cierre => 
      cierre.club_id === id 
        ? { ...cierre, [field]: parseFloat(value) || 0 }
        : cierre
    ));
  };

  const calcularTotal = (cierre) => {
    const admin = calcularAdmin(cierre.cuota, cierre.club_id); // Ya es negativo
    // TOTAL = GAN+CTA + TICKET + SPIN + COMP_JACK + ADMIN(negativo) + OVERLAY + DEV_PERDIDAS + LUCKY_DRAGON
    // NO incluye CUOTA ni JACKPOT
    return cierre.gan_cta + 
           cierre.ticket + 
           cierre.spin + 
           cierre.comp_jack + 
           admin +  // Ya es negativo, se resta
           cierre.overlay + 
           cierre.devolucion_perdidas + 
           cierre.lucky_dragon +
           bjValor(cierre);
  };

  const calcularTotales = () => {
    return cierres.reduce((acc, cierre) => {
      const admin = calcularAdmin(cierre.cuota, cierre.club_id);
      return {
        gan_cta: acc.gan_cta + cierre.gan_cta,
        cuota: acc.cuota + cierre.cuota,
        jackpot: acc.jackpot + cierre.jackpot,
        ticket: acc.ticket + cierre.ticket,
        spin: acc.spin + cierre.spin,
        comp_jack: acc.comp_jack + cierre.comp_jack,
        administracion: acc.administracion + admin, // Suma de todos los admin (negativos)
        overlay: acc.overlay + cierre.overlay,
        devolucion_perdidas: acc.devolucion_perdidas + cierre.devolucion_perdidas,
        lucky_dragon: acc.lucky_dragon + cierre.lucky_dragon,
        blackjack: acc.blackjack + bjValor(cierre),
        total: acc.total + calcularTotal(cierre)
      };
    }, {
      gan_cta: 0,
      cuota: 0,
      jackpot: 0,
      ticket: 0,
      spin: 0,
      comp_jack: 0,
      administracion: 0,
      overlay: 0,
      devolucion_perdidas: 0,
      lucky_dragon: 0,
      blackjack: 0,
      total: 0
    });
  };

  // Función para exportar PDF de un club individual - VERSIÓN PROFESIONAL
  // soloRetornar=true devuelve el PDF en base64 (para mandarlo a Telegram) en vez
  // de descargarlo. Es el MISMO PDF 'CONSOLIDACIÓN DE CLUB' que se manda siempre.
  const exportarPDFClub = (cierre, soloRetornar = false) => new Promise((resolve) => {
    const doc = new jsPDF();
    const admin = calcularAdmin(cierre.cuota, cierre.club_id);
    const total = calcularTotal(cierre);
    
    // Logo EvoGameTech (cargado desde public)
    const logoUrl = '/evogametech_logo.jpg';
    
    // === ENCABEZADO PRINCIPAL ===
    // Título centrado y prominente
    doc.setFontSize(24);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(124, 58, 237); // Violeta corporativo
    doc.text('CONSOLIDACIÓN DE CLUB', 105, 22, { align: 'center' });
    
    // Espaciado después del título
    let yPos = 35;
    
    // === INFORMACIÓN DEL CLUB ===
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('Club:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(cierre.club_name, 40, yPos);
    
    yPos += 8;
    doc.setFont(undefined, 'bold');
    doc.setTextColor(40, 40, 40);
    doc.text('ID Club:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(String(cierre.club_id), 40, yPos);
    
    // Línea separadora sutil
    yPos += 5;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(20, yPos, 190, yPos);
    
    // === INFORMACIÓN TEMPORAL ===
    yPos += 8;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    
    // Formatear fecha correctamente (sin "!")
    const fechaDesdeFormat = fechaDesde.split('-').reverse().join('/');
    const fechaHastaFormat = fechaHasta.split('-').reverse().join('/');
    doc.text(`Período: ${fechaDesdeFormat} - ${fechaHastaFormat}`, 20, yPos);
    
    yPos += 6;
    const fechaGeneracion = new Date().toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    doc.text(`Generado: ${fechaGeneracion}`, 20, yPos);
    
    // Línea separadora más fuerte antes de la tabla
    yPos += 5;
    doc.setDrawColor(160, 160, 160);
    doc.setLineWidth(0.5);
    doc.line(20, yPos, 190, yPos);
    
    // === TABLA DE DATOS ===
    yPos += 10;
    const tableStartY = yPos;
    const lineHeight = 9;
    const colLeftX = 25;
    const colRightX = 185;
    const tableWidth = 170;
    
    // Header de tabla con mejor diseño
    doc.setFillColor(124, 58, 237);
    doc.rect(20, yPos - 2, tableWidth, 11, 'F');
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('CONCEPTO', colLeftX, yPos + 5);
    doc.text('VALOR', colRightX, yPos + 5, { align: 'right' });
    
    yPos += 13;
    
    // Datos de la tabla
    // Campos marcados con * son solo referenciales (no impactan en el TOTAL)
    const tableData = [
      { label: 'GAN + CTA', value: formatNumber(cierre.gan_cta), isReference: false },
      { label: 'CUOTA *', value: formatNumber(cierre.cuota), isReference: true },
      { label: 'JACKPOT *', value: formatNumber(cierre.jackpot), isReference: true },
      { label: 'TICKET', value: formatNumber(cierre.ticket), isReference: false },
      { label: 'SPIN', value: formatNumber(cierre.spin), isReference: false },
      { label: 'COMP JACK', value: formatNumber(cierre.comp_jack), isReference: false },
      { label: `ADMIN (${getPorcentajeAdmin(cierre.club_id)}%)`, value: formatNumber(admin), isReference: false },
      { label: 'OVERLAY', value: formatNumber(cierre.overlay), isReference: false },
      { label: 'DEV. PÉRDIDAS', value: formatNumber(cierre.devolucion_perdidas), isReference: false },
      { label: 'LUCKY DRAGON', value: formatNumber(cierre.lucky_dragon), isReference: false },
      { label: `BLACKJACK (${bjPorcentaje(cierre)}%)`, value: formatNumber(bjValor(cierre)), isReference: false },
      ...comisionesFilasPDF(cierre)
    ];
    
    doc.setFontSize(10);
    
    tableData.forEach((row, idx) => {
      // Fondo alternado para mejor legibilidad
      if (idx % 2 === 0) {
        doc.setFillColor(248, 248, 250);
        doc.rect(20, yPos - 6, tableWidth, lineHeight, 'F');
      }
      
      // Borde sutil de separación
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.1);
      doc.line(20, yPos + 2, 190, yPos + 2);
      
      // Colores según si es referencial o no
      if (row.isReference) {
        // Campos referenciales en gris suave
        doc.setFont(undefined, 'normal');
        doc.setTextColor(160, 160, 160); // Gris suave
      } else {
        // Campos que impactan en el total
        doc.setFont(undefined, 'bold');
        doc.setTextColor(40, 40, 40);
      }
      doc.text(row.label, colLeftX, yPos);
      
      // Valor (derecha, alineado perfectamente)
      if (row.isReference) {
        doc.setFont(undefined, 'normal');
        doc.setTextColor(180, 180, 180); // Gris más suave para valores
      } else {
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
      }
      doc.text(row.value, colRightX, yPos, { align: 'right' });
      
      yPos += lineHeight;
    });
    
    // Borde final de la tabla
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(20, yPos, 190, yPos);
    
    // === TOTAL DESTACADO ===
    yPos += 8;
    
    // Color según el resultado
    if (total < 0) {
      doc.setFillColor(239, 68, 68); // Rojo para negativo
    } else {
      doc.setFillColor(34, 197, 94); // Verde para positivo
    }
    
    // Box del total con mejor proporción
    doc.roundedRect(20, yPos, tableWidth, 16, 2, 2, 'F');
    
    doc.setFontSize(15);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('TOTAL:', colLeftX, yPos + 11);
    doc.text(formatNumber(total), colRightX, yPos + 11, { align: 'right' });
    
    // === PIE DE PÁGINA ===
    // Nota aclaratoria sobre campos referenciales
    doc.setFontSize(7.5);
    doc.setFont(undefined, 'italic');
    doc.setTextColor(160, 160, 160);
    doc.text('* Campos marcados con asterisco son solo referenciales y no impactan en el cálculo del TOTAL', 105, 274, { align: 'center' });
    
    // Nombre del archivo y cierre del PDF (descarga o base64)
    const finalizar = () => {
      const clubNameClean = cierre.club_name.replace(/[^a-zA-Z0-9]/g, '_');
      const fechaDesdeFormat = fechaDesde.split('-').reverse().join('/');
      const fechaHastaFormat = fechaHasta.split('-').reverse().join('/');
      const fileName = `Consolidacion_${clubNameClean}_${fechaDesdeFormat.replace(/\//g, '-')}_${fechaHastaFormat.replace(/\//g, '-')}.pdf`;
      if (soloRetornar) {
        resolve({ pdf_base64: doc.output('datauristring').split(',')[1], pdf_nombre: fileName });
        return;
      }
      doc.save(fileName);
      toast.success(`✓ PDF exportado: ${cierre.club_name}`);
      resolve(null);
    };

    // Logo EvoGameTech (grande, centrado, completamente visible)
    try {
      // Cargar logo como imagen
      const img = new Image();
      img.src = logoUrl;
      img.onload = () => {
        // Logo grande centrado (50mm de ancho para mejor visibilidad, altura proporcional)
        const logoWidth = 50;
        const logoHeight = (img.height / img.width) * logoWidth;
        const logoX = (210 - logoWidth) / 2; // Centrado en página A4 (210mm)
        const logoY = 277; // Posición ajustada para máxima visibilidad sin corte
        doc.addImage(img, 'JPEG', logoX, logoY, logoWidth, logoHeight);
        
        // Descargar después de cargar el logo
        finalizar();
      };
      img.onerror = () => {
        // Si falla cargar el logo, solo texto
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(160, 160, 160);
        doc.text('EvoGameTech', 105, 285, { align: 'center' });
        
        finalizar();
      };
    } catch (error) {
      console.error('Error cargando logo:', error);
      // Fallback: texto simple
      doc.setFontSize(7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(160, 160, 160);
      doc.text('EvoGameTech', 105, 285, { align: 'center' });
      
      finalizar();
    }
  });


  const handleConsolidar = async () => {
    if (!fechaDesde || !fechaHasta) {
      toast.error('Selecciona el rango de fechas');
      return;
    }

    if (cierres.length === 0) {
      toast.error('No hay datos para consolidar');
      return;
    }

    setLoading(true);
    try {
      // Capturar la tabla antes de consolidar
      let tablaCapturaBase64 = null;
      if (screenshotRefTotal.current) {
        const canvas = await html2canvas(screenshotRefTotal.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          useCORS: true
        });
        tablaCapturaBase64 = canvas.toDataURL('image/png');
      }

      // Preparar clubs con porcentajes admin y totales actuales
      const clubsConTotal = cierres.map(cierre => {
        const admin = calcularAdmin(cierre.cuota, cierre.club_id);
        const total = calcularTotal(cierre);
        return {
          club_id: cierre.club_id,
          club_name: cierre.club_name,
          gan_cta: cierre.gan_cta,
          cuota: cierre.cuota,
          jackpot: cierre.jackpot,
          ticket: cierre.ticket,
          spin: cierre.spin,
          comp_jack: cierre.comp_jack,
          administracion_porcentaje: getPorcentajeAdmin(cierre.club_id),
          administracion: admin,
          overlay: cierre.overlay,
          devolucion_perdidas: cierre.devolucion_perdidas,
          lucky_dragon: cierre.lucky_dragon,
          blackjack_resultado: cierre.blackjack_resultado || 0,
          blackjack_porcentaje: bjPorcentaje(cierre),
          blackjack: bjValor(cierre),
          total: total
        };
      });

      const response = await axios.post(`${API}/poker/consolidacion/guardar`, {
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        clubs_data: clubsConTotal,
        comp_jack_total: compJackTotal,
        overlay_total: overlayTotal,
        devolucion_perdidas_total: devolucionPerdidasTotal,
        tabla_captura: tablaCapturaBase64 // Enviar captura
      });

      // Actualizar la tabla con los valores distribuidos del backend
      if (response.data.comisiones_acreditadas?.length) {
        response.data.comisiones_acreditadas.forEach((c) =>
          toast.success(`Comisión ${c.origen} acreditada a ${c.cliente}: $${formatNumber(c.monto_ars)}`));
      }
      if (response.data.clubs_data) {
        setCierres(response.data.clubs_data);
      }

      toast.success(`✅ Cierre consolidado. Total: ${formatNumber(response.data.total_general)}`);
      setShowModalConsolidar(false);
      
      // Limpiar datos
      setCompJackTotal(0);
      setOverlayTotal(0);
      setDevolucionPerdidasTotal(0);
    } catch (error) {
      console.error('Error consolidando:', error);
      toast.error('Error al consolidar el cierre');
    } finally {
      setLoading(false);
    }
  };

  const loadHistorico = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/poker/consolidacion/historico`);
      setHistorico(response.data.cierres || []);
      setShowHistorico(true);
      // Marcar qué cierres ya se enviaron a Telegram
      try {
        const { data } = await axios.get(`${API}/cierre/enviados`);
        const mapa = {};
        (data.enviados || []).forEach((e) => {
          mapa[`${e.cierre_id}:${e.club_id}`] = { enviado: true, enviado_at: e.enviado_at };
        });
        setEnviosCierre(mapa);
      } catch (e) {
        console.error('No pude cargar los cierres ya enviados:', e);
      }
    } catch (error) {
      console.error('Error cargando histórico:', error);
      toast.error('Error al cargar histórico');
    } finally {
      setLoading(false);
    }
  };

  const eliminarCierre = async (cierreId) => {
    if (!window.confirm('¿Estás seguro de eliminar este cierre? Esta acción no se puede deshacer.')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/poker/consolidacion/${cierreId}`);
      toast.success('Cierre eliminado correctamente');
      // Recargar histórico
      const response = await axios.get(`${API}/poker/consolidacion/historico`);
      setHistorico(response.data.cierres || []);
    } catch (error) {
      console.error('Error eliminando cierre:', error);
      toast.error('Error al eliminar cierre');
    } finally {
      setLoading(false);
    }
  };

  const capturarTotalTabla = async () => {
    if (!screenshotRefTotal.current) return;
    
    try {
      const canvas = await html2canvas(screenshotRefTotal.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `total_consolidacion_${fechaDesde}_${fechaHasta}.png`;
      link.href = image;
      link.click();
      
      toast.success('Captura guardada correctamente');
    } catch (error) {
      console.error('Error capturando:', error);
      toast.error('Error al capturar screenshot');
    }
  };

  // Generar PDF individual de un club desde el histórico
  // Enviar el cierre de un club a su grupo de Telegram y agendar el movimiento.
  // Manda el MISMO PDF 'CONSOLIDACIÓN DE CLUB' que se descarga con el botón de al lado.
  const enviarCierreClub = async (cierre) => {
    const periodo = `${fechaDesde}_${fechaHasta}`;
    const clave = `${periodo}:${cierre.club_id}`;
    const total = calcularTotal(cierre);
    const yaEnviado = enviosCierre[clave]?.enviado;

    // Antes de mandar, preguntar al backend a qué cliente y a qué grupo va a llegar
    let destino = null;
    try {
      const { data } = await axios.get(`${API}/cierre/destino/${cierre.club_id}`);
      destino = data;
    } catch (error) {
      const msg = error?.response?.data?.detail || 'No pude resolver el destino de este club';
      toast.error(typeof msg === 'string' ? msg : 'No pude resolver el destino');
      return;
    }

    const cabecera = yaEnviado
      ? `⚠️ El cierre de ${cierre.club_name} YA se envió en este período.\n\nSi seguís, se reenvía el PDF pero NO se duplica el movimiento ni se toca el balance.\n\n`
      : '';
    const ars = total * 300;
    // Plan de movimientos: hay clubes que se agendan en otro cliente o se ajustan a cero
    let plan = `📊 ${destino.cliente_name}: ${formatNumber(destino.balance)} → ${formatNumber(destino.balance + ars)}\n`;
    if (destino.agendar_extra) {
      plan += `📊 ${destino.agendar_extra.cliente_name}: ${formatNumber(destino.agendar_extra.balance)} → ${formatNumber(destino.agendar_extra.balance + ars)}\n`;
    }
    if (destino.ajustar_a_cero) {
      plan += `🔄 ${destino.cliente_name}: ajuste ${formatNumber(-ars)} → queda en ${formatNumber(destino.balance)}\n`;
      plan += `   (${destino.ajuste_concepto})\n`;
    }
    const confirmacion =
      `${cabecera}Enviar el cierre de ${cierre.club_name}?\n\n` +
      `📄 PDF al grupo: ${destino.grupo_nombre || destino.telegram_group_id}\n` +
      `Período: ${fechaDesde} al ${fechaHasta}\n` +
      `Total: ${formatNumber(total)} PP = ${formatNumber(ars)} ARS\n\n` +
      plan + `\n¿Confirmás?`;
    if (!window.confirm(confirmacion)) return;

    try {
      setEnviandoClub(clave);
      const doc = await exportarPDFClub(cierre, true);
      if (!doc?.pdf_base64) {
        toast.error('No pude generar el PDF');
        return;
      }

      const { data } = await axios.post(`${API}/cierre/enviar-club`, {
        cierre_id: periodo,
        club_id: cierre.club_id,
        club_name: cierre.club_name,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        total_pp: total,
        pdf_base64: doc.pdf_base64,
        pdf_nombre: doc.pdf_nombre,
      });

      if (data.ya_enviado) {
        toast.success(`📤 PDF reenviado a ${data.cliente_name}. El movimiento no se duplicó.`);
      } else {
        const movs = (data.movimientos || [])
          .map((m) => `${m.cliente_name} ${m.monto >= 0 ? '+' : ''}${formatNumber(m.monto)}`)
          .join(' · ');
        toast.success(`✅ Enviado a ${data.cliente_name} · ${movs}`);
      }
      setEnviosCierre((prev) => ({
        ...prev,
        [clave]: { enviado: true, enviado_at: data.enviado_at || new Date().toISOString() },
      }));
    } catch (error) {
      const msg = error?.response?.data?.detail || 'No se pudo enviar el cierre';
      toast.error(typeof msg === 'string' ? msg : 'No se pudo enviar el cierre');
    } finally {
      setEnviandoClub(null);
    }
  };

  // Deshacer un cierre ya enviado: borra los mensajes del grupo, revierte los
  // movimientos y saca la marca de enviado del histórico.
  const deshacerCierreClub = async (cierre) => {
    const periodo = `${fechaDesde}_${fechaHasta}`;
    const clave = `${periodo}:${cierre.club_id}`;
    const total = calcularTotal(cierre);
    const ars = total * 300;

    const confirmacion =
      `⚠️ DESHACER el cierre de ${cierre.club_name}?\n\n` +
      `Se va a:\n` +
      `• Borrar el PDF y el mensaje del grupo de Telegram\n` +
      `• Eliminar el movimiento agendado (${formatNumber(ars)} ARS)\n` +
      `• Devolver el balance como estaba\n` +
      `• Sacar la marca de enviado del histórico\n\n` +
      `Telegram solo permite borrar mensajes de hasta 48 horas.\n\n¿Confirmás?`;
    if (!window.confirm(confirmacion)) return;

    try {
      setEnviandoClub(clave);
      const { data } = await axios.post(`${API}/cierre/deshacer-club`, {
        cierre_id: periodo,
        club_id: cierre.club_id,
      });

      const revertidos = (data.movimientos_revertidos || [])
        .map((m) => `${m.cliente_name} ${formatNumber(m.monto_revertido)}`)
        .join(' · ');
      toast.success(
        `↩️ Cierre deshecho${revertidos ? ` · ${revertidos}` : ''}`,
        { duration: 6000 }
      );
      if (data.sin_message_ids) {
        toast.warning('Este cierre se envió antes de esta función: los mensajes del grupo hay que borrarlos a mano.', { duration: 9000 });
      } else if (data.mensajes_fallidos > 0) {
        toast.warning(`No pude borrar ${data.mensajes_fallidos} mensaje(s) del grupo (pueden tener más de 48hs). Revisalos a mano.`, { duration: 9000 });
      }
      setEnviosCierre((prev) => {
        const next = { ...prev };
        delete next[clave];
        return next;
      });
    } catch (error) {
      const msg = error?.response?.data?.detail || 'No se pudo deshacer el cierre';
      toast.error(typeof msg === 'string' ? msg : 'No se pudo deshacer el cierre');
    } finally {
      setEnviandoClub(null);
    }
  };

  const generarPDFClub = async (cierre, clubData, soloRetornar = false) => {
    try {
      if (!soloRetornar) toast.info('Generando PDF del club...');
      
      const jsPDF = (await import('jspdf')).default;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      let y = 25;
      
      // Insertar logo en el encabezado
      try {
        const logoUrl = '/unitedpoker_logo.jpg';
        const logoWidth = 30;
        const logoHeight = 30;
        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(logoUrl, 'JPEG', logoX, 10, logoWidth, logoHeight);
        y = 48;
      } catch (error) {
        console.log('No se pudo cargar el logo:', error);
        y = 25;
      }
      
      // Header - Título principal
      pdf.setFillColor(139, 92, 246); // Color violeta
      pdf.rect(0, y - 5, pageWidth, 15, 'F');
      pdf.setTextColor(255, 255, 255); // Texto blanco
      pdf.setFontSize(18);
      pdf.setFont(undefined, 'bold');
      pdf.text('INFORME DE CIERRE DE CICLO', pageWidth / 2, y + 5, { align: 'center' });
      pdf.setTextColor(0, 0, 0); // Restaurar color negro
      y += 18;
      
      // Período con guión en lugar de flecha
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Período: ${cierre.fecha_desde} - ${cierre.fecha_hasta}`, pageWidth / 2, y, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      y += 15;
      
      // Info del Club con fondo gris claro
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(15, y - 5, pageWidth - 30, 30, 3, 3, 'F');
      
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('INFORMACIÓN DEL CLUB', 20, y);
      y += 8;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Club: ${clubData.club_name}`, 20, y);
      y += 6;
      pdf.text(`ID Club: ${clubData.club_id}`, 20, y);
      y += 6;
      pdf.text(`Consolidado el: ${new Date(cierre.fecha_consolidacion).toLocaleString('es-AR')}`, 20, y);
      y += 6;
      pdf.text(`Por: ${cierre.consolidado_por}`, 20, y);
      y += 14;
      
      // Detalle de Valores con diseño mejorado
      pdf.setFillColor(139, 92, 246);
      pdf.rect(0, y - 5, pageWidth, 10, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('DETALLE DE VALORES', 20, y);
      pdf.setTextColor(0, 0, 0);
      y += 12;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      
      const valores = [
        ['GAN+CTA', formatNumber(clubData.gan_cta), clubData.gan_cta < 0],
        ['CUOTA', formatNumber(clubData.cuota), false],
        ['JACKPOT', formatNumber(clubData.jackpot), clubData.jackpot < 0],
        ['TICKET', formatNumber(clubData.ticket), clubData.ticket < 0],
        ['SPIN (3%)', formatNumber(clubData.spin), clubData.spin < 0],
        ['COMP JACKPOT', formatNumber(clubData.comp_jack), clubData.comp_jack < 0],
        [`ADMIN (${clubData.administracion_porcentaje}%)`, formatNumber(clubData.administracion), clubData.administracion < 0],
        ['OVERLAY', formatNumber(clubData.overlay), clubData.overlay < 0],
        ['DEV. PÉRDIDAS', formatNumber(clubData.devolucion_perdidas), clubData.devolucion_perdidas < 0],
        ['LUCKY DRAGON', formatNumber(clubData.lucky_dragon), clubData.lucky_dragon < 0],
        [`BLACKJACK (${bjPorcentaje(clubData)}%)`, formatNumber(bjValor(clubData)), bjValor(clubData) < 0],
        ...comisionesFilasPDFClub(clubData)
      ];
      
      // Alternar colores de fondo para mejor lectura
      valores.forEach(([label, value, isNegative], index) => {
        if (index % 2 === 0) {
          pdf.setFillColor(249, 250, 251);
          pdf.rect(15, y - 4, pageWidth - 30, 7, 'F');
        }
        
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        pdf.text(label + ':', 20, y);
        
        // Color rojo para valores negativos
        if (isNegative) {
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(220, 38, 38);
        } else {
          pdf.setFont(undefined, 'normal');
          pdf.setTextColor(0, 0, 0);
        }
        
        pdf.text(value, pageWidth - 20, y, { align: 'right' });
        pdf.setTextColor(0, 0, 0);
        y += 7;
      });
      
      // Total con diseño destacado
      y += 8;
      pdf.setDrawColor(139, 92, 246);
      pdf.setLineWidth(0.5);
      pdf.line(15, y, pageWidth - 15, y);
      y += 10;
      
      const totalColor = clubData.total < 0 ? [220, 38, 38] : [22, 163, 74];
      pdf.setFillColor(...totalColor, 20);
      pdf.roundedRect(15, y - 6, pageWidth - 30, 12, 2, 2, 'F');
      
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(...totalColor);
      pdf.text('TOTAL:', 20, y);
      pdf.text(formatNumber(clubData.total), pageWidth - 20, y, { align: 'right' });
      pdf.setTextColor(0, 0, 0);
      y += 15;
      
      // Nota de movimiento con estilo
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'bold');
      const movimiento = clubData.total < 0 ? 'DÉBITO' : 'CRÉDITO';
      pdf.text(`Movimiento a registrar: ${movimiento} de ${formatNumber(Math.abs(clubData.total))}`, pageWidth / 2, y, { align: 'center' });
      
      // Footer mejorado
      const footerY = pdf.internal.pageSize.getHeight() - 15;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(20, footerY - 5, pageWidth - 20, footerY - 5);
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Este documento es para uso interno y registro de movimientos', pageWidth / 2, footerY, { align: 'center' });
      pdf.text('UnitedPoker - Unión de Uniones', pageWidth / 2, footerY + 4, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      
      // Guardar
      const nombrePDF = `Informe_${clubData.club_name.replace(/\s/g, '_')}_${cierre.fecha_desde}_${cierre.fecha_hasta}.pdf`;
      if (soloRetornar) {
        // Mismo PDF que se descarga, pero en base64 para mandarlo a Telegram
        const dataUri = pdf.output('datauristring');
        return { pdf_base64: dataUri.split(',')[1], pdf_nombre: nombrePDF };
      }
      pdf.save(nombrePDF);
      
      toast.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('Error al generar PDF');
    }
  };

  const totales = calcularTotales();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FileSpreadsheet className="w-8 h-8 text-violet-600" />
            Consolidación - Cierre de Ciclos
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gestión de cierres semanales de clubes
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={loadDatosCalculados}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Cargar Datos
          </button>

          {isAdmin && (<button
            onClick={() => setShowModalConsolidar(true)}
            disabled={loading || cierres.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" />
            Consolidar Cierre
          </button>)}

          {isAdmin && (<button
            onClick={capturarTotalTabla}
            disabled={cierres.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Camera className="w-4 h-4" />
            Capturar Total
          </button>)}

          <button
            onClick={loadHistorico}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            <History className="w-4 h-4" />
            Ver Histórico
          </button>

          {isAdmin && (<button
            onClick={() => setShowConfigSubClubs(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
            Config Sub-clubs
          </button>)}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Período del Cierre Semanal
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha Desde
            </label>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha Hasta
            </label>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="flex items-end">
            <div className="w-full bg-violet-100 dark:bg-violet-900/40 rounded-lg px-4 py-3 text-center">
              <div className="text-xs text-violet-600 dark:text-violet-400 font-medium mb-1">Período Seleccionado</div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">
                {fechaDesde && fechaHasta ? `${fechaDesde} → ${fechaHasta}` : 'Selecciona fechas'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla de Cierre de Ciclos */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
              <tr>
                <th className="px-4 py-3 text-left font-semibold sticky left-0 bg-gradient-to-r from-violet-600 to-purple-600 z-10">NOMBRE</th>
                <th className="px-4 py-3 text-left font-semibold">ID</th>
                <th className="px-4 py-3 text-right font-semibold">GAN+CTA</th>
                <th className="px-4 py-3 text-right font-semibold bg-violet-400/50">CUOTA</th>
                <th className="px-4 py-3 text-right font-semibold bg-violet-400/50">JACKPOT</th>
                <th className="px-4 py-3 text-right font-semibold">TICKET</th>
                <th className="px-4 py-3 text-right font-semibold">SPIN</th>
                <th className="px-4 py-3 text-right font-semibold">COMP JACK</th>
                <th className="px-4 py-3 text-center font-semibold">ADMIN %</th>
                <th className="px-4 py-3 text-right font-semibold">OVERLAY</th>
                <th className="px-4 py-3 text-right font-semibold">DEV. PÉRDIDAS</th>
                <th className="px-4 py-3 text-right font-semibold">LUCKY DRAGON</th>
                <th className="px-4 py-3 text-right font-semibold">BLACKJACK</th>
                <th className="px-4 py-3 text-right font-semibold bg-gradient-to-r from-green-600 to-emerald-600">TOTAL</th>
                <th className="px-4 py-3 text-center font-semibold">ACCIONES</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {cierres.map((cierre, idx) => (
                <tr key={cierre.club_id} className={`hover:bg-violet-50 dark:hover:bg-violet-900/10 ${idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                  <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white sticky left-0 bg-inherit">
                    {cierre.club_name}
                    <ComisionesReferenteBadge cierre={cierre} />
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono text-xs">
                    {cierre.club_id}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-white">
                    {formatNumber(cierre.gan_cta)}
                  </td>
                  <td className="px-4 py-3 text-right bg-violet-100 dark:bg-violet-900/20">
                    <span className="font-semibold text-violet-900 dark:text-violet-300">
                      {formatNumber(cierre.cuota)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right bg-violet-100 dark:bg-violet-900/20">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatNumber(cierre.jackpot)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-gray-900 dark:text-white">
                      {formatNumber(cierre.ticket)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-gray-900 dark:text-white">
                      {formatNumber(cierre.spin)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-gray-500 dark:text-gray-400">
                      {formatNumber(cierre.comp_jack)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center bg-yellow-50 dark:bg-yellow-900/20">
                    <div className="flex items-center justify-center gap-2">
                      {editandoAdminClub === cierre.club_id ? (
                        <>
                          <input
                            type="number"
                            step="0.1"
                            value={getPorcentajeAdmin(cierre.club_id)}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setPorcentajesAdmin(prev => ({ ...prev, [cierre.club_id]: val }));
                            }}
                            className="w-16 px-2 py-1 text-xs text-center text-gray-900 border border-orange-300 rounded"
                          />
                          <button
                            onClick={() => guardarPorcentajeAdminClub(cierre.club_id, getPorcentajeAdmin(cierre.club_id))}
                            className="text-green-600 hover:text-green-700"
                            title="Guardar"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => setEditandoAdminClub(null)}
                            className="text-red-600 hover:text-red-700"
                            title="Cancelar"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditandoAdminClub(cierre.club_id)}
                            className="text-xs bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/40 dark:hover:bg-orange-900/60 px-3 py-1 rounded font-semibold text-orange-700 dark:text-orange-300"
                            title="Configurar %"
                          >
                            {getPorcentajeAdmin(cierre.club_id)}%
                          </button>
                          <span className="font-bold text-red-600 dark:text-red-400">
                            {formatNumber(calcularAdmin(cierre.cuota, cierre.club_id))}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-gray-500 dark:text-gray-400">
                      {formatNumber(cierre.overlay)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-gray-500 dark:text-gray-400">
                      {formatNumber(cierre.devolucion_perdidas)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-gray-900 dark:text-white">
                      {formatNumber(cierre.lucky_dragon)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <BlackjackCell cierre={cierre} onSaved={loadDatosCalculados} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-bold text-lg ${calcularTotal(cierre) < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {formatNumber(calcularTotal(cierre))}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        onClick={() => exportarPDFClub(cierre)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors"
                        title="Descargar el PDF de consolidación"
                        data-testid={`pdf-club-${cierre.club_id}`}
                      >
                        <FileDown className="w-3.5 h-3.5" />
                        PDF
                      </button>
                      {(() => {
                        const clave = `${fechaDesde}_${fechaHasta}:${cierre.club_id}`;
                        const yaEnviado = enviosCierre[clave]?.enviado;
                        const enviando = enviandoClub === clave;
                        return (
                          <>
                          <button
                            onClick={() => enviarCierreClub(cierre)}
                            disabled={enviando}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 text-white text-xs rounded-lg transition-colors disabled:opacity-50 ${yaEnviado ? 'bg-gray-400 hover:bg-gray-500' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                            title={yaEnviado
                              ? 'Ya se envió en este período. Reenviar manda el PDF sin duplicar el movimiento'
                              : 'Enviar el cierre al grupo de Telegram del club y agendar el movimiento'}
                            data-testid={`enviar-club-${cierre.club_id}`}
                          >
                            {enviando ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            {yaEnviado ? 'Reenviar' : 'Enviar'}
                          </button>
                          {yaEnviado && (
                            <button
                              onClick={() => deshacerCierreClub(cierre)}
                              disabled={enviando}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-lg transition-colors disabled:opacity-50"
                              title="Deshacer: borra el mensaje del grupo, elimina el movimiento y devuelve el balance"
                              data-testid={`deshacer-club-${cierre.club_id}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              Deshacer
                            </button>
                          )}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot ref={screenshotRefTotal} className="bg-gradient-to-r from-violet-700 to-purple-700 text-white font-bold">
              <tr>
                <td colSpan="2" className="px-4 py-4 text-left text-base sticky left-0 bg-gradient-to-r from-violet-700 to-purple-700">
                  TOTALES ({cierres.length} clubs)
                </td>
                <td className="px-4 py-4 text-right text-base">{formatNumber(totales.gan_cta)}</td>
                <td className="px-4 py-4 text-right text-base bg-violet-600/80">{formatNumber(totales.cuota)}</td>
                <td className="px-4 py-4 text-right text-base bg-violet-600/80">{formatNumber(totales.jackpot)}</td>
                <td className="px-4 py-4 text-right text-base">{formatNumber(totales.ticket)}</td>
                <td className="px-4 py-4 text-right text-base">{formatNumber(totales.spin)}</td>
                <td className="px-4 py-4 text-right text-base">{formatNumber(totales.comp_jack)}</td>
                <td className="px-4 py-4 text-right text-base">{formatNumber(totales.administracion)}</td>
                <td className="px-4 py-4 text-right text-base">{formatNumber(totales.overlay)}</td>
                <td className="px-4 py-4 text-right text-base">{formatNumber(totales.devolucion_perdidas)}</td>
                <td className="px-4 py-4 text-right text-base">{formatNumber(totales.lucky_dragon)}</td>
                <td className="px-4 py-4 text-right text-base">{formatNumber(totales.blackjack)}</td>
                <td className="px-4 py-4 text-right">
                  <span className={`text-xl ${totales.total < 0 ? 'text-red-200' : 'text-green-200'}`}>
                    {formatNumber(totales.total)}
                  </span>
                </td>
                <td className="px-4 py-4 text-center"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Info */}
      {cierres.length === 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-lg p-6 text-center">
          <FileSpreadsheet className="w-12 h-12 mx-auto text-yellow-600 dark:text-yellow-400 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No hay datos cargados
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Selecciona un rango de fechas y haz clic en "Cargar Datos"
          </p>
        </div>
      )}

      {/* Leyenda de Fórmulas de Distribución */}
      {cierres.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border-2 border-blue-200 dark:border-blue-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            📐 Fórmulas de Distribución (Al Consolidar)
          </h3>
          
          <div className="space-y-3 text-sm">
            <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
              <div className="font-semibold text-red-600 dark:text-red-400 mb-1">
                OVERLAY (Negativo - Se distribuye proporcionalmente a CUOTA)
              </div>
              <code className="text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                overlay_club = overlay_total × (cuota_club / suma_cuotas)
              </code>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                → El que más cuota genera, más paga
              </p>
            </div>

            <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
              <div className="font-semibold text-green-600 dark:text-green-400 mb-1">
                DEVOLUCIÓN PÉRDIDAS (Positivo - Solo para clubes con GAN+CTA negativo)
              </div>
              <code className="text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                Si GAN+CTA {'<'} 0: dev_club = dev_total × (|gan_cta_club| / suma_perdidas)
              </code>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                → El que más perdió, más recibe. Clubes positivos NO reciben
              </p>
            </div>

            <div className="bg-white dark:bg-gray-700 rounded-lg p-3">
              <div className="font-semibold text-purple-600 dark:text-purple-400 mb-1">
                COMP JACKPOT (+ o - - Se distribuye proporcionalmente a CUOTA)
              </div>
              <code className="text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                comp_jack_club = comp_jack_total × (cuota_club / suma_cuotas)
              </code>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                → El que más cuota genera, más recibe (o más paga si es negativo)
              </p>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
            <p className="text-xs text-gray-600 dark:text-gray-400 italic">
              💡 Estas distribuciones se aplican automáticamente al hacer clic en "Consolidar Cierre"
            </p>
          </div>
        </div>
      )}

      {/* Modal Consolidar */}
      {showModalConsolidar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 rounded-t-lg">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <CheckCircle className="w-6 h-6" />
                Consolidar Cierre Semanal
              </h2>
              <p className="text-white/80 text-sm mt-1">
                {fechaDesde} a {fechaHasta}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Compensación Jackpot Total
                  <span className="text-xs text-gray-500 ml-2">(puede ser positivo o negativo)</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={compJackTotal}
                  onChange={(e) => setCompJackTotal(e.target.value === '' ? 0 : Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Ej: 454 o -200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Overlay Total
                  <span className="text-xs text-red-500 ml-2">(generalmente negativo)</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={overlayTotal}
                  onChange={(e) => setOverlayTotal(e.target.value === '' ? 0 : Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Ej: -1500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Devolución Pérdidas Total
                  <span className="text-xs text-green-500 ml-2">(siempre positivo)</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={devolucionPerdidasTotal}
                  onChange={(e) => setDevolucionPerdidasTotal(e.target.value === '' ? 0 : Math.abs(Number(e.target.value)))}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Ej: 3000"
                />
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Total General:</div>
                <div className={`text-2xl font-bold ${totales.total < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatNumber(totales.total)}
                </div>
              </div>
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setShowModalConsolidar(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConsolidar}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Guardando...' : 'Consolidar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Histórico */}
      {showHistorico && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <History className="w-6 h-6" />
                  Histórico de Cierres
                </h2>
                <button
                  onClick={() => setShowHistorico(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {historico.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    No hay cierres consolidados
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {historico.map((cierre) => (
                    <div key={cierre.id} className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-purple-600" />
                            <h3 className="font-bold text-gray-900 dark:text-white">
                              {cierre.fecha_desde} → {cierre.fecha_hasta}
                            </h3>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                            <p>📅 Consolidado: {new Date(cierre.fecha_consolidacion).toLocaleDateString('es-AR', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</p>
                            <p>👤 Usuario: {cierre.consolidado_por}</p>
                            <p>🏢 Clubs: {cierre.clubs_data?.length || 0}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="text-right">
                            <div className="text-xs text-gray-600 dark:text-gray-400">Total General</div>
                            <div className={`text-2xl font-bold ${cierre.total_general < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {formatNumber(cierre.total_general)}
                            </div>
                          </div>
                          <button
                            onClick={() => eliminarCierre(cierre.id)}
                            className="flex items-center gap-1 px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors"
                            title="Eliminar cierre"
                          >
                            <Trash2 className="w-3 h-3" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                      
                      {/* Resumen de valores ingresados */}
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-purple-200 dark:border-purple-700 mb-3">
                        <div className="text-xs">
                          <span className="text-gray-500">Comp Jack:</span>
                          <span className={`ml-1 font-semibold ${cierre.comp_jack_total < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatNumber(cierre.comp_jack_total || 0)}
                          </span>
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-500">Overlay:</span>
                          <span className="ml-1 font-semibold text-red-600">
                            {formatNumber(cierre.overlay_total || 0)}
                          </span>
                        </div>
                        <div className="text-xs">
                          <span className="text-gray-500">Dev Pérd:</span>
                          <span className="ml-1 font-semibold text-green-600">
                            {formatNumber(cierre.devolucion_perdidas_total || 0)}
                          </span>
                        </div>
                      </div>

                      {/* Captura de la tabla */}
                      {cierre.tabla_captura && (
                        <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-semibold">📸 Captura de Totales:</p>
                          <img 
                            src={cierre.tabla_captura} 
                            alt="Captura de totales" 
                            className="w-full rounded-lg border-2 border-gray-300 dark:border-gray-600"
                          />
                        </div>
                      )}

                      {/* PDFs por club */}
                      <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-semibold">📄 Informes por Club:</p>
                        <div className="flex flex-wrap gap-2">
                          {cierre.clubs_data?.map((club) => (
                            <button
                              key={club.club_id}
                              onClick={() => generarPDFClub(cierre, club)}
                              className="flex items-center gap-1 px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded transition-colors"
                              title={`Generar PDF de ${club.club_name}`}
                            >
                              <FileText className="w-3 h-3" />
                              {club.club_name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Configuración Sub-clubs */}
      {showConfigSubClubs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-orange-600 to-amber-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Settings className="w-6 h-6" />
                  Configuración de Sub-clubs
                </h2>
                <button onClick={() => setShowConfigSubClubs(false)} className="text-white hover:bg-white/20 rounded-lg p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-orange-100 text-sm mt-1">
                Configura qué clubs están asociados a cada cliente. Esta configuración queda guardada permanentemente.
              </p>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Relaciones existentes */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-orange-600" />
                  Relaciones Configuradas
                </h3>
                {relationships.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No hay relaciones configuradas</p>
                ) : (
                  <div className="space-y-3">
                    {relationships.map((rel) => (
                      <div key={rel.id} className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-700">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-bold text-gray-900 dark:text-white">{rel.cliente_name}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              Club principal: <span className="font-medium">{rel.club_name || "N/A"}</span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              Sub-clubs: <span className="font-medium">{rel.sub_clubs?.map(s => s.club_name).join(", ") || "Ninguno"}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => ejecutarCierreTelegram(rel.cliente_id, rel.cliente_name)}
                              disabled={enviandoCierres || !fechaDesde || !fechaHasta}
                              className="flex items-center gap-1 px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm rounded-lg"
                              title="Enviar cierres a Telegram"
                            >
                              <Send className="w-3 h-3" />
                              {enviandoCierres ? "Enviando..." : "Enviar Cierres"}
                            </button>
                            <button
                              onClick={() => eliminarRelationship(rel.id)}
                              className="flex items-center gap-1 px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Agregar nueva relación */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-600" />
                  Agregar Nueva Configuración
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Cliente (UNITED)
                    </label>
                    <select
                      value={selectedCliente}
                      onChange={(e) => setSelectedCliente(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Seleccionar cliente...</option>
                      {clientesUnited.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Club Principal
                    </label>
                    <select
                      value={selectedClubPrincipal}
                      onChange={(e) => setSelectedClubPrincipal(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Seleccionar club...</option>
                      {clubsDisponibles.map((c) => (
                        <option key={c.club_id} value={c.club_id}>{c.club_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sub-clubs (opcional)
                    </label>
                    <select
                      multiple
                      value={selectedSubClubs}
                      onChange={(e) => setSelectedSubClubs(Array.from(e.target.selectedOptions, opt => opt.value))}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white h-32"
                    >
                      {clubsDisponibles.map((c) => (
                        <option key={c.club_id} value={c.club_id}>{c.club_name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Ctrl+click para seleccionar múltiples</p>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={guardarConfigSubClubs}
                    disabled={loadingConfig || !selectedCliente || !selectedClubPrincipal}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg"
                  >
                    <Save className="w-4 h-4" />
                    {loadingConfig ? "Guardando..." : "Guardar Configuración"}
                  </button>
                </div>
              </div>

              {/* Nota informativa */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Nota:</strong> Para enviar cierres a Telegram, primero debes:
                </p>
                <ol className="text-sm text-blue-700 dark:text-blue-300 list-decimal list-inside mt-2 space-y-1">
                  <li>Seleccionar las fechas del cierre en la pantalla principal</li>
                  <li>Asegurarte de que existe un cierre guardado para esas fechas</li>
                  <li>Hacer click en "Enviar Cierres" junto al cliente configurado</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
